import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { CookieConsent } from "@/components/CookieConsent";
import { ScrollToTop } from "@/components/ScrollToTop";
import { GoogleAnalytics } from "@/components/GoogleAnalytics";

// Import Index directly (critical landing page - no lazy loading for faster LCP)
import Index from "./pages/Index";

// Lazy load other pages for code splitting
const Services = lazy(() => import("./pages/Services"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const FAQ = lazy(() => import("./pages/FAQ"));
const About = lazy(() => import("./pages/About"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Contact = lazy(() => import("./pages/Contact"));
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Referral = lazy(() => import("./pages/Referral"));
const Terms = lazy(() => import("./pages/Terms"));
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions"));
const ReferralTerms = lazy(() => import("./pages/ReferralTerms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const AdminLayout = lazy(() => import("./layouts/AdminLayout"));
const Analytics = lazy(() => import("./pages/admin/Analytics"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ReferralRedirect = lazy(() => import("./pages/ReferralRedirect"));

// New admin pages
const ReferralDashboard = lazy(() => import("./pages/admin/referral/ReferralDashboard"));
const ReferralFinancial = lazy(() => import("./pages/admin/referral/ReferralFinancial"));
const ReferralUsers = lazy(() => import("./pages/admin/referral/ReferralUsers"));
const Forms = lazy(() => import("./pages/admin/Forms"));

const queryClient = new QueryClient();

// Minimal loading fallback
const PageLoader = () => (
  <div className="min-h-screen bg-deep-navy flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-neon-glow border-t-transparent rounded-full animate-spin" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <GoogleAnalytics />
        <CookieConsent />
        <Suspense fallback={<PageLoader />}>
          <main>
            <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/services" element={<Services />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/about" element={<About />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/referral" element={<Referral />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
            <Route path="/referral-terms" element={<ReferralTerms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            
            {/* Admin Routes - Protected */}
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<ReferralDashboard />} />
              <Route path="referral/dashboard" element={<ReferralDashboard />} />
              <Route path="referral/financial" element={<ReferralFinancial />} />
              <Route path="referral/users" element={<ReferralUsers />} />
              
              <Route path="forms" element={<Forms />} />
              <Route path="analytics" element={<Analytics />} />
            </Route>
            
            <Route path="/r/:code" element={<ReferralRedirect />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
            </Routes>
          </main>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
